import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SITE from '../../api';
import useSEO from '../../useSEO';
import { useSite } from '../../components/StoreLayout';
import { STORE_BASE } from '../../routeBase';
import { ProductCard } from '../Home';
import { FaceShapeIcon } from './faceShapeIcons';

// Standard dispensing-optician face-shape guidance, mapped onto the shape
// vocabulary that actually exists on product_variants.shape (Square,
// Rectangle, Cat Eye, Geometric, Round, Oval, Aviator, Wayfarer) so every
// recommendation below is a real, filterable catalog shape — never a shape
// this store doesn't stock.
const FACE_SHAPES = [
  {
    key: 'oval', label: 'Oval',
    traits: 'Balanced proportions — forehead slightly wider than the jaw, gently rounded chin.',
    recommend: ['Rectangle', 'Geometric', 'Wayfarer'],
    avoid: ['Oversized frames that overwhelm already-balanced proportions'],
    tip: 'The most versatile face shape — structured frames add definition without fighting the natural balance.',
  },
  {
    key: 'round', label: 'Round',
    traits: 'Soft curves, similar width and length, no strong angles at the jaw or cheekbones.',
    recommend: ['Square', 'Rectangle', 'Geometric'],
    avoid: ['Round', 'Oval'],
    tip: 'Angular frames add definition that a round face lacks on its own — round-on-round just doubles the curve.',
  },
  {
    key: 'square', label: 'Square',
    traits: 'Strong, angular jawline, broad forehead, minimal curve at the jaw corners.',
    recommend: ['Round', 'Oval', 'Cat Eye'],
    avoid: ['Square', 'Geometric'],
    tip: 'Curved frames soften a strong jawline; another angular frame only reinforces it.',
  },
  {
    key: 'heart', label: 'Heart',
    traits: 'Wider forehead and cheekbones, narrowing to a pointed or slender chin.',
    recommend: ['Cat Eye', 'Oval', 'Aviator'],
    avoid: ['Square', 'Geometric'],
    tip: 'A frame that\'s wider at the bottom balances a narrower chin — the classic reason aviators work so well here.',
  },
  {
    key: 'diamond', label: 'Diamond',
    traits: 'Narrow forehead and jawline with the widest point at the cheekbones.',
    recommend: ['Cat Eye', 'Oval', 'Round'],
    avoid: ['Square', 'Geometric'],
    tip: 'Curved brow lines soften prominent cheekbones instead of competing with them.',
  },
  {
    key: 'oblong', label: 'Oblong / Long',
    traits: 'Face length noticeably greater than width, with a long, straight cheek line.',
    recommend: ['Round', 'Square', 'Wayfarer'],
    avoid: ['Rectangle', 'Narrow, small frames — they lengthen the face further'],
    tip: 'A frame with more depth (taller lens) and decorative or bold temples adds width and breaks up the length.',
  },
];

// Undertone colour guidance is deliberately advisory copy, not a live
// product filter — product_variants.color_name only has real descriptive
// values (Gold, Copper, Silver, Gunmetal, Black, Blue…) on ~11% of active
// SKUs, so hard-filtering the results grid by colour on top of shape would
// sometimes go empty. Shape stays the one guaranteed, always-populated
// filter; colour is guidance text an optician can act on in person.
const UNDERTONES = [
  {
    key: 'warm', label: 'Warm',
    desc: 'Golden, peachy or olive undertones — gold jewellery is usually the flattering choice.',
    swatches: ['#c99a3e', '#8b5a2b', '#b06a35'],
    colorNote: 'gold, copper, brown and warm tortoise frames',
  },
  {
    key: 'cool', label: 'Cool',
    desc: 'Pink, red or blue undertones — silver jewellery is usually the flattering choice.',
    swatches: ['#9aa5ad', '#3a3f44', '#5b7a99'],
    colorNote: 'silver, gunmetal, black and cool blue-grey frames',
  },
  {
    key: 'neutral', label: 'Neutral',
    desc: 'A balanced mix of warm and cool — most metal tones sit equally well on you.',
    swatches: ['#c9a08a', '#3a3f44', '#c9c9c9'],
    colorNote: 'rose gold, black and crystal-clear frames',
  },
];

// frame_type is a clean, well-populated field (Full Rim / Half Rim /
// Rimless on virtually every active SKU) — safe to use as a real secondary
// filter, unlike colour. Youthful deliberately sets no frame_type: "bold"
// is a shape/colour trait already covered above, not a construction type,
// so forcing one here would narrow results without adding real signal.
const STYLES = [
  { key: 'professional', label: 'Professional', icon: 'briefcase', desc: 'Polished, workplace-ready looks.', frame_type: 'Full Rim', note: 'structured full-rim frames in classic tones' },
  { key: 'sporty', label: 'Sporty', icon: 'activity', desc: 'Active, and built to keep up with you.', frame_type: 'Half Rim', note: 'lightweight half-rim or rimless frames built for movement' },
  { key: 'youthful', label: 'Youthful', icon: 'sparkles', desc: 'Bold, trend-forward and fun.', frame_type: '', note: 'bold full-rim frames in statement colours' },
];

const STEP_LABELS = { 1: 'Face Shape', 2: 'Skin Undertone', 3: 'Style' };

function ProgressBar({ step }) {
  const n = typeof step === 'number' ? step : 4;
  return (
    <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginBottom: 10 }}>
      {[1, 2, 3].map(i => (
        <span key={i} style={{
          width: 34, height: 4, borderRadius: 2,
          background: i <= n ? 'var(--sf-primary)' : '#e5e5e5',
        }} />
      ))}
    </div>
  );
}

function pickCardStyle(isActive) {
  return {
    display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 10,
    border: isActive ? '1.5px solid var(--sf-primary)' : '1.5px solid #e5e5e5',
    background: isActive ? 'var(--sf-primary)' : '#fff',
    color: isActive ? '#fff' : 'var(--sf-primary)',
    borderRadius: 16, padding: '22px 16px', cursor: 'pointer', width: '100%',
  };
}

export default function FaceShapeAdvisor() {
  const { content: c } = useSite();
  useSEO({
    title: c['tools.face-shape.meta_title'] || 'Face Shape Frame Advisor for Opticians',
    description: c['tools.face-shape.meta_description'] || 'Free face shape to frame shape guide for dispensing opticians — point customers to the eyeglass frame shapes that actually suit them, with real matching models from our wholesale catalog.',
    keywords: c['tools.face-shape.meta_keywords'] || 'face shape frame guide, best glasses for face shape, frame shape advisor, optician tools, eyewear styling guide',
    canonical: `${window.location.origin}/store/tools/face-shape-advisor`,
  });

  const [step, setStep] = useState(1);
  const [shape, setShape] = useState(null);
  const [undertone, setUndertone] = useState(null);
  const [style, setStyle] = useState(null);
  const [products, setProducts] = useState(null);
  const [loading, setLoading] = useState(false);

  const [email, setEmail] = useState('');
  const [hp, setHp] = useState('');
  const [emailStatus, setEmailStatus] = useState('idle'); // idle | busy | done | error | dismissed

  useEffect(() => {
    if (step !== 'results' || !shape) return;
    let cancelled = false;
    setLoading(true);
    const shapeKey = shape.recommend[0];
    const fetchWith = params => SITE.get('/products', { params: { shape: shapeKey, limit: 8, sort: 'newest', ...params } })
      .then(r => r.data.products || []).catch(() => []);
    (async () => {
      let list = style?.frame_type ? await fetchWith({ frame_type: style.frame_type }) : [];
      if (list.length < 4) list = await fetchWith({});
      if (!cancelled) { setProducts(list); setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [step, shape, style]);

  const chooseShape = fs => { setShape(fs); setStep(2); };
  const chooseUndertone = u => { setUndertone(u); setStep(3); };
  const chooseStyle = st => { setStyle(st); setStep('results'); };
  const restart = () => {
    setStep(1); setShape(null); setUndertone(null); setStyle(null); setProducts(null);
    setEmailStatus('idle'); setEmail('');
  };

  const submitEmail = async e => {
    e.preventDefault();
    if (hp) return; // honeypot tripped — silently no-op
    setEmailStatus('busy');
    try {
      await SITE.post('/newsletter-subscribe', { email, hp });
      setEmailStatus('done');
    } catch (err) {
      setEmailStatus('error');
    }
  };

  return (
    <div style={{ background: 'var(--sf-body-bg)' }}>
      {/* HERO */}
      <section style={{ maxWidth: 900, margin: '0 auto', padding: '48px 24px 16px', textAlign: 'center' }}>
        <Link to={`${STORE_BASE}/tools`} style={{ fontSize: 12.5, color: 'var(--sf-hover)', fontWeight: 600, textDecoration: 'none' }}>
          <i className="ti ti-arrow-left" style={{ fontSize: 13, verticalAlign: -1 }} /> All free tools
        </Link>
        <h1 style={{ fontSize: 'clamp(24px,3.6vw,34px)', fontWeight: 800, color: 'var(--sf-primary)', margin: '14px 0 12px' }}>
          Face Shape &amp; Style Advisor
        </h1>
        <p style={{ fontSize: 14.5, color: 'var(--sf-muted)', maxWidth: 620, margin: '0 auto', lineHeight: 1.7 }}>
          A quick 3-step guide to point a customer at frames that actually suit them — face shape, skin undertone
          and style, matched against real models from our wholesale catalog.
        </p>
      </section>

      <section style={{ maxWidth: 900, margin: '24px auto 0', padding: '0 24px' }}>

        {step !== 'results' && (
          <>
            <ProgressBar step={step} />
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <span style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: 'var(--sf-hover)' }}>
                Step {step} of 3 — {STEP_LABELS[step]}
              </span>
              {step > 1 && (
                <div>
                  <button onClick={() => setStep(step - 1)} style={{
                    border: 'none', background: 'none', color: 'var(--sf-muted)', fontSize: 12, cursor: 'pointer', marginTop: 6, padding: 0,
                  }}>
                    <i className="ti ti-arrow-left" style={{ fontSize: 12, verticalAlign: -1 }} /> Back
                  </button>
                </div>
              )}
            </div>
          </>
        )}

        {/* STEP 1 — FACE SHAPE */}
        {step === 1 && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(96px, 1fr))', gap: 10, marginBottom: 8 }} className="fsa-picker">
            {FACE_SHAPES.map(fs => (
              <button key={fs.key} onClick={() => chooseShape(fs)} style={pickCardStyle(false)}>
                <FaceShapeIcon shape={fs.key} size={40} />
                <span style={{ fontSize: 12.5, fontWeight: 700 }}>{fs.label}</span>
              </button>
            ))}
          </div>
        )}

        {/* STEP 2 — SKIN UNDERTONE */}
        {step === 2 && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14, marginBottom: 8 }}>
            {UNDERTONES.map(u => (
              <button key={u.key} onClick={() => chooseUndertone(u)} style={pickCardStyle(false)}>
                <div style={{ display: 'flex', gap: 6 }}>
                  {u.swatches.map((sw, i) => (
                    <span key={i} style={{ width: 22, height: 22, borderRadius: '50%', background: sw, border: '2px solid #fff', boxShadow: '0 0 0 1px #e5e5e5' }} />
                  ))}
                </div>
                <span style={{ fontSize: 14, fontWeight: 700 }}>{u.label}</span>
                <span style={{ fontSize: 11.5, lineHeight: 1.5, opacity: 0.85 }}>{u.desc}</span>
              </button>
            ))}
          </div>
        )}

        {/* STEP 3 — STYLE */}
        {step === 3 && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14, marginBottom: 8 }}>
            {STYLES.map(st => (
              <button key={st.key} onClick={() => chooseStyle(st)} style={pickCardStyle(false)}>
                <i className={`ti ti-${st.icon}`} style={{ fontSize: 28 }} />
                <span style={{ fontSize: 14, fontWeight: 700 }}>{st.label}</span>
                <span style={{ fontSize: 11.5, lineHeight: 1.5, opacity: 0.85 }}>{st.desc}</span>
              </button>
            ))}
          </div>
        )}

        {/* RESULTS */}
        {step === 'results' && shape && undertone && style && (
          <>
            <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 14, padding: 24, marginBottom: 28, display: 'flex', gap: 20 }} className="fsa-guidance-card">
              <div style={{
                flexShrink: 0, width: 88, height: 88, borderRadius: '50%',
                background: 'color-mix(in srgb, var(--sf-accent) 16%, #fff)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <FaceShapeIcon shape={shape.key} size={54} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h3 style={{ fontSize: 17, fontWeight: 800, color: 'var(--sf-primary)', margin: '0 0 8px' }}>
                  {shape.label} Face &middot; {undertone.label} Undertone &middot; {style.label}
                </h3>
                <p style={{ fontSize: 13.5, color: 'var(--sf-muted)', lineHeight: 1.7, margin: '0 0 16px' }}>{shape.traits}</p>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }} className="fsa-guidance">
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--sf-hover)', marginBottom: 8 }}>
                      Recommended shapes
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {shape.recommend.map(s => (
                        <span key={s} style={{
                          fontSize: 12, fontWeight: 700, color: 'var(--sf-primary)',
                          background: 'color-mix(in srgb, var(--sf-accent) 20%, #fff)', borderRadius: 20, padding: '5px 12px',
                        }}>{s}</span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: '#dc2626', marginBottom: 8 }}>
                      Best avoided
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {shape.avoid.map(s => (
                        <span key={s} style={{ fontSize: 12, fontWeight: 600, color: '#b91c1c', background: '#fef2f2', borderRadius: 20, padding: '5px 12px' }}>{s}</span>
                      ))}
                    </div>
                  </div>
                </div>

                <p style={{ fontSize: 12.5, color: 'var(--sf-primary)', background: '#f7f8fa', borderRadius: 8, padding: '10px 12px', marginBottom: 8, lineHeight: 1.6 }}>
                  <i className="ti ti-palette" style={{ fontSize: 13, verticalAlign: -1, marginRight: 4 }} />
                  <b>Colour:</b> look for {undertone.colorNote}.
                </p>
                <p style={{ fontSize: 12.5, color: 'var(--sf-primary)', background: '#f7f8fa', borderRadius: 8, padding: '10px 12px', margin: 0, lineHeight: 1.6 }}>
                  <i className="ti ti-bulb" style={{ fontSize: 13, verticalAlign: -1, marginRight: 4 }} />
                  <b>Style:</b> {style.note}. {shape.tip}
                </p>
              </div>
            </div>

            {/* MATCHING MODELS */}
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
              <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--sf-primary)', margin: 0 }}>
                {shape.recommend[0]} models to show this customer
              </h3>
              <Link to={`${STORE_BASE}/shop?shape=${encodeURIComponent(shape.recommend[0])}`} style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--sf-hover)', textDecoration: 'none' }}>
                See all {shape.recommend[0]} models <i className="ti ti-arrow-right" style={{ fontSize: 12, verticalAlign: -1 }} />
              </Link>
            </div>
            {loading && !products && <div style={{ fontSize: 13, color: 'var(--sf-muted)', padding: '24px 0' }}>Loading matching models…</div>}
            {products && products.length === 0 && <div style={{ fontSize: 13, color: 'var(--sf-muted)', padding: '24px 0' }}>No models currently in stock for this shape.</div>}
            {products && products.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 16, marginBottom: 32 }}>
                {products.map(p => <ProductCard key={p.model_no} p={p} cols={4} fontScale="sm" />)}
              </div>
            )}

            {/* EMAIL CAPTURE — our own newsletter opt-in, skippable, never blocks the results above */}
            {emailStatus !== 'dismissed' && (
              <div style={{ background: 'var(--sf-primary)', borderRadius: 14, padding: '22px 24px', marginBottom: 32, color: '#fff' }}>
                {emailStatus === 'done' ? (
                  <p style={{ margin: 0, fontSize: 13.5 }}>
                    <i className="ti ti-check" style={{ verticalAlign: -1, marginRight: 6 }} /> Thanks — you're on the list for style tips and offers.
                  </p>
                ) : (
                  <>
                    <h4 style={{ fontSize: 14.5, fontWeight: 700, margin: '0 0 4px' }}>Want more style guides like this?</h4>
                    <p style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.75)', margin: '0 0 14px' }}>
                      Occasional frame styling tips and offers from Salvador Eyewear — no spam, unsubscribe any time.
                    </p>
                    <form onSubmit={submitEmail} style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <input type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="Your email address"
                        style={{ flex: '1 1 220px', border: 'none', borderRadius: 8, padding: '10px 14px', fontSize: 13 }} />
                      <input type="text" value={hp} onChange={e => setHp(e.target.value)} tabIndex={-1} autoComplete="off"
                        style={{ position: 'absolute', left: -9999, width: 1, height: 1, opacity: 0 }} aria-hidden="true" />
                      <button type="submit" disabled={emailStatus === 'busy'} style={{
                        border: 'none', background: '#fff', color: 'var(--sf-primary)', borderRadius: 8,
                        padding: '10px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                      }}>
                        {emailStatus === 'busy' ? 'Submitting…' : 'Sign up'}
                      </button>
                      <button type="button" onClick={() => setEmailStatus('dismissed')} style={{
                        border: 'none', background: 'none', color: 'rgba(255,255,255,0.7)', fontSize: 12, cursor: 'pointer',
                      }}>No thanks</button>
                    </form>
                    {emailStatus === 'error' && <p style={{ fontSize: 12, color: '#fecaca', marginTop: 8 }}>Couldn't sign up — please try again.</p>}
                  </>
                )}
              </div>
            )}

            <div style={{ textAlign: 'center', marginBottom: 8 }}>
              <button onClick={restart} style={{ border: 'none', background: 'none', color: 'var(--sf-hover)', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}>
                <i className="ti ti-refresh" style={{ fontSize: 13, verticalAlign: -1, marginRight: 4 }} /> Start over
              </button>
            </div>
          </>
        )}
      </section>

      {/* CTA BANNER */}
      <section style={{ background: 'var(--sf-primary)', color: '#fff', marginTop: 24 }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '32px 24px', display: 'flex', flexWrap: 'wrap', gap: 20, alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h3 style={{ fontSize: 17, fontWeight: 800, margin: '0 0 6px' }}>Stock up your shop for less</h3>
            <p style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.75)', margin: 0, maxWidth: 460 }}>
              View our latest wholesale frame collections — acetate, TR90 and metal, shipped worldwide.
            </p>
          </div>
          <Link to={`${STORE_BASE}/shop`} style={{
            background: '#fff', color: 'var(--sf-primary)', padding: '11px 22px', borderRadius: 8,
            fontWeight: 700, fontSize: 13, textDecoration: 'none', flexShrink: 0,
          }}>
            Browse Wholesale Catalog
          </Link>
        </div>
      </section>

      <style>{`
        .fsa-picker button:hover, .fsa-guidance-card + div button:hover { transform: translateY(-2px); }
        @media (max-width: 640px) {
          .fsa-guidance { grid-template-columns: 1fr !important; }
          .fsa-guidance-card { flex-direction: column; align-items: flex-start; }
        }
      `}</style>
    </div>
  );
}
